import { useState, useEffect, createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useSpaceContext } from './space';
import client from '../utilities/client';
import {
  createCommentCache,
  deleteCommentCache,
  updateCommentBodyCache,
  createSpaceCache,
  deleteSpaceCache,
  updateSpaceCache,
  createSpaceMemberCache,
  deleteSpaceMemberCache,
  updateSpaceMemberCache,
  createTagCache,
  deleteTagCache,
  updateTagCache,
  createTopicCache,
  deleteTopicCache,
  updateTopicCache,
  deleteViewerCache,
  updateViewerCache,
  createSessionCache,
  deleteSessionCache,
} from '../utilities/cache';

export const SyncContext = createContext();

export const SyncProvider = ({
  viewerId,
  spaceId,
  spaceRole,
  spaceSlug,
  children,
}) => {
  const params = useParams();
  const [ws, setWs] = useState();
  const { spaceVars } = useSpaceContext();

  const [isDisconnected, setIsDisconnected] = useState();

  useEffect(() => {
    const activityHandler = () => {
      if (!document.hidden && isDisconnected) {
        // Reset cache and refetch queries
        client.resetStore();

        console.log('[Sync] Cache store reset');

        setIsDisconnected(false);
      }
    };

    document.addEventListener('visibilitychange', activityHandler);

    return () => {
      document.removeEventListener('visibilitychange', activityHandler);
    };
  });

  useEffect(() => {
    const socket = io(import.meta.env.VITE_SYNC_URL, {
      path: '/sync',
      auth: {
        token: localStorage.getItem('token'),
      },
      transports: ['websocket'],
    });

    setWs(socket);

    socket.on('connect', () => {
      console.log(`[Sync] Connected ${new Date().toISOString()}`);
    });

    socket.on('disconnect', (reason) => {
      console.log(`[Sync] Disconnected ${new Date().toISOString()}`);

      // User was disconnected from the socket because they didn't use the tab for a while
      if (reason === 'ping timeout' || reason === 'transport error') {
        // Store disconnection in state
        setIsDisconnected(true);
      }
    });
  }, []);

  useEffect(() => {
    if (ws && spaceId) {
      ws.emit('SPACE', spaceId);

      ws.on('COMMENT_CREATE', (args) => {
        createCommentCache(args.topicId, client.cache, args);
      });

      ws.on('COMMENT_DELETE', (args) => {
        deleteCommentCache(params.s, args.topicPublicId, client.cache, args);
      });

      ws.on('COMMENT_UPDATE', (args) => {
        updateCommentBodyCache(client.cache, args);
      });

      ws.on('SPACE_CREATE', (args) => {
        createSpaceCache(client.cache, args);
      });

      ws.on('SPACE_DELETE', (args) => {
        deleteSpaceCache(params.s, client.cache, args);
      });

      ws.on('SPACE_UPDATE', (args) => {
        updateSpaceCache(client.cache, args, spaceId, spaceRole, spaceSlug);
      });

      ws.on('SPACE_USER_CREATE', (args) => {
        createSpaceMemberCache(params.s, client.cache, args);
      });

      ws.on('SPACE_USER_DELETE', (args) => {
        deleteSpaceMemberCache(params.s, client.cache, args, viewerId);
      });

      ws.on('SPACE_USER_UPDATE', (args) => {
        updateSpaceMemberCache(client.cache, args, viewerId);
      });

      ws.on('TAG_CREATE', (args) => {
        createTagCache(params.s, client.cache, args);
      });

      ws.on('TAG_DELETE', (args) => {
        deleteTagCache(params.s, client.cache, args);
      });

      ws.on('TAG_UPDATE', (args) => {
        updateTagCache(client.cache, args);
      });

      ws.on('TOPIC_CREATE', (args) => {
        createTopicCache(args.spaceId, client.cache, args);
      });

      ws.on('TOPIC_DELETE', (args) => {
        deleteTopicCache(spaceVars, params.s, client.cache, args);
      });

      ws.on('TOPIC_UPDATE', (args) => {
        updateTopicCache(spaceVars, params.s, client.cache, args);
      });
    }
  }, [ws, spaceId, spaceRole, spaceSlug]);

  useEffect(() => {
    if (ws) {
      ws.on('VIEWER_DELETE', () => {
        deleteViewerCache();
      });

      ws.on('VIEWER_SPACE_CREATE', (args) => {
        createSpaceCache(client.cache, args);
      });

      ws.on('VIEWER_SPACE_JOIN', (args) => {
        createSpaceCache(client.cache, args);
      });

      ws.on('VIEWER_SPACE_LEAVE', (args) => {
        deleteSpaceCache(params.s, client.cache, args);
      });

      ws.on('VIEWER_UPDATE', (args) => {
        updateViewerCache(client.cache, args);
      });

      ws.on('SESSION_CREATE', (args) => {
        createSessionCache(client.cache, args);
      });

      ws.on('SESSION_DELETE', (args) => {
        deleteSessionCache(client.cache, args);
      });
    }
  }, [ws]);

  return <SyncContext.Provider value={ws}>{children}</SyncContext.Provider>;
};

export const useSyncContext = () => useContext(SyncContext);
